import { lessonToWorkout } from 'app/utils/workouts';
import { RestDayWorkout } from 'app/pages/progressive-plan/components';
import type { IntlShape } from 'react-intl';
import type { ProgressivePlanBlock, ProgressivePlanWeek, ProgressivePlanWorkout } from 'app/types/graphql';
import type { Workout } from 'app/ui/components/molecules/workout-list';

export interface PlanContext {
  currentBlockOrder: number;
  currentWeekId?: string;
  currentWeekNumber: number;
  todaysWorkoutId?: string;
  todaysWorkoutWeekId?: string;
  todaysDayOfWeek: number;
  tomorrowsWorkoutId?: string;
  tomorrowsWorkoutWeekId?: string;
}

/**
 * Reorders weeks within a block: [Current Week, ...Future Weeks, ...Past Weeks]
 */
export const getOrderedWeeks = (
  weeks: Partial<ProgressivePlanWeek>[],
  isCurrentBlock: boolean,
  currentWeekId?: string,
) => {
  if (!isCurrentBlock || !currentWeekId) {
    return weeks;
  }
  const index = weeks.findIndex((w) => w?.id === currentWeekId);
  if (index === -1) {
    return weeks;
  }

  return [weeks[index], ...weeks.slice(index + 1), ...weeks.slice(0, index)];
};

/**
 * Prepares and sorts the workout list for a week.
 * Sorting Priority: Today (0) > Tomorrow (1) > Upcoming (2) > Done (3)
 */
export const prepareWorkoutList = (
  week: Partial<ProgressivePlanWeek>,
  block: Partial<ProgressivePlanBlock>,
  context: PlanContext,
  intl: IntlShape,
) => {
  const {
    todaysWorkoutWeekId,
    tomorrowsWorkoutWeekId,
    currentWeekId,
    currentBlockOrder,
    currentWeekNumber,
    todaysWorkoutId,
    tomorrowsWorkoutId,
    todaysDayOfWeek,
  } = context;

  const isTodaysWeek = week.id === todaysWorkoutWeekId;
  const isTomorrowsWeek = week.id === tomorrowsWorkoutWeekId;
  const isCurrentWeek = week.id === currentWeekId;

  const blockOrder = block.displayOrder ?? 0;
  const weekNum = week.weekNumber ?? 0;

  // A week is "past" if it's logically before the current one,
  // but we keep Today/Tomorrow's weeks "active" for better UX.
  const isPastWeek =
    (blockOrder < currentBlockOrder || (blockOrder === currentBlockOrder && weekNum < currentWeekNumber)) &&
    !isTodaysWeek &&
    !isTomorrowsWeek;

  const workoutList = (week.workouts || [])
    .filter((w): w is ProgressivePlanWorkout => !!w)
    .map(({ id, dayOfWeek, lesson }: ProgressivePlanWorkout) => {
      const isToday = isTodaysWeek && id === todaysWorkoutId;
      const isTomorrow = isTomorrowsWeek && id === tomorrowsWorkoutId;

      // Logic: A workout is done if it's in a past week, or earlier in the current week.
      // We NEVER mark Today or Tomorrow as done.
      const isPastWorkout = (isPastWeek || (isCurrentWeek && dayOfWeek < todaysDayOfWeek)) && !isToday && !isTomorrow;

      const workout = lesson ? lessonToWorkout(lesson, { isDone: isPastWorkout }) : RestDayWorkout;

      let sortWeight = 2; // Default: Upcoming
      if (isToday) {
        sortWeight = 0;
        workout.label = intl.formatMessage({ id: 'progressivePlan.today', defaultMessage: 'Today' });
      } else if (isTomorrow) {
        sortWeight = 1;
        workout.label = intl.formatMessage({ id: 'progressivePlan.tomorrow', defaultMessage: 'Tomorrow' });
      } else if (workout.isDone) {
        sortWeight = 3;
      }

      return { ...workout, sortWeight };
    });

  // Stable sort by priority weight
  const sortedWorkoutList = [...workoutList]
    .sort((a, b) => a.sortWeight - b.sortWeight)
    .map(({ sortWeight, ...workout }) => workout as Workout);

  return { workoutList: sortedWorkoutList, isPastWeek };
};
